import { openDialog } from "./Application";
import { t } from "./i18n";
import { antiforgeryHeaders } from "./Csrf";

function getUiLanguage() {
    return (document.documentElement.lang || "es").toLowerCase();
}

function getApiUrl(path) {
    return `/${getUiLanguage()}/api/${path}`;
}

function createTopUpActionError(message) {
    const language = getUiLanguage();
    const error = new Error(message);
    error.actionUrl = `/${language}/payment?balance=true`;
    error.actionText = t("Top up balance");
    return error;
}

function createEmailCaptureError(communityId, storyUrl) {
    const error = new Error(t("You need an account to continue."));
    error.emailCapture = { community: communityId, url: storyUrl };
    return error;
}

async function isAuthenticated(communityId) {
    const fallback = document.body?.dataset?.authenticated === "true";

    try {
        const response = await fetch(
            getApiUrl(`user/status?community=${encodeURIComponent(Number.isFinite(communityId) ? communityId : 0)}`),
            {
                method: "GET",
                credentials: "same-origin",
                headers: {
                    "Accept": "application/json",
                    "Cache-Control": "no-cache"
                }
            }
        );

        if (!response.ok) {
            return false;
        }

        const payload = await response.json();
        if (typeof payload?.authenticated === "boolean") {
            return payload.authenticated;
        }
    } catch {
    }

    return fallback;
}

async function buyStory(storyId, communityId, storyUrl) {
    const response = await fetch(getApiUrl("story-access/buy"), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            ...antiforgeryHeaders()
        },
        credentials: "same-origin",
        body: JSON.stringify({ storyId, communityId })
    });

    if (!response.ok) {
        let body = null;
        try {
            body = await response.json();
        } catch {
        }
        const message = body?.message || t("The payment could not be processed.");

        if (body?.requiredCredits != null) {
            throw createTopUpActionError(message);
        }

        // No session: capture the email to send the signup link and
        // resume the purchase after topping up balance.
        if (response.status === 401) {
            throw createEmailCaptureError(communityId, storyUrl);
        }

        // Generic failure with an active session: offer the balance top-up.
        if (!body?.message) {
            throw createTopUpActionError(message);
        }

        throw new Error(message);
    }

    return response.json();
}

function updateCommunityBalance(communityId, communityBalance) {
    if (!Number.isFinite(communityBalance) || !Number.isFinite(communityId)) {
        return;
    }

    let elements = document.querySelectorAll(`.community-balance-value[data-community-id="${communityId}"]`);
    if (elements.length === 0 && communityBalance > 0) {
        const card = document.querySelector(`.card[data-id="${communityId}"]`);
        if (card) {
            const paragraph = document.createElement("p");
            const strong = document.createElement("strong");
            strong.className = "value community-balance-value";
            strong.dataset.communityId = String(communityId);
            paragraph.append(t("Accumulated prize:") + " ", strong);

            const actions = card.querySelector(".community-play-button")?.closest("div");
            if (actions) {
                card.insertBefore(paragraph, actions);
            } else {
                card.appendChild(paragraph);
            }

            elements = card.querySelectorAll(`.community-balance-value[data-community-id="${communityId}"]`);
        }
    }

    elements.forEach((element) => {
        if (communityBalance <= 0) {
            const paragraph = element.closest("p");
            if (paragraph) {
                paragraph.style.display = "none";
            }
            return;
        }

        const paragraph = element.closest("p");
        if (paragraph) {
            paragraph.style.display = "";
        }
        element.textContent = `$${communityBalance.toFixed(2)}`;
    });
}

async function getPlayRoute(communityId) {
    const response = await fetch(getApiUrl(`competition/community/${communityId}/play`), {
        method: "POST",
        credentials: "same-origin",
        headers: {
            "Accept": "application/json",
            ...antiforgeryHeaders()
        }
    });

    if (!response.ok) {
        throw new Error(t("The competition could not be started."));
    }

    const payload = await response.json();
    if (!payload?.story?.url || !payload?.story?.id) {
        throw new Error(t("No available story could be found."));
    }

    return payload.story;
}

function initPlayButtons() {
    const buttons = document.querySelectorAll(".community-play-button");
    buttons.forEach((button) => {
        button.addEventListener("click", async (event) => {
            event.preventDefault();

            const communityId = Number(button.dataset.communityId);
            if (!communityId) {
                return;
            }

            const communityTitle = button.dataset.communityTitle || t("Story");
            const communityPrice = Number(button.dataset.communityPrice);

            // If the user is NOT authenticated, show the signup dialog
            // immediately. Preparing the competition (daily rewards, competition
            // creation and title translation) is expensive and only needed to
            // build the email link, so it is resolved in the background
            // while the visitor types their email: the dialog no longer waits for it.
            if (!(await isAuthenticated(communityId))) {
                const urlPromise = getPlayRoute(communityId)
                    .then((story) => story?.url || null)
                    .catch(() => null);

                openDialog(
                    "email",
                    {
                        community: communityId,
                        urlPromise
                    },
                    t("Create your account to continue"),
                    "small"
                );
                return;
            }

            // If the user IS authenticated, show the purchase warning
            button.disabled = true;
            try {
                const playPayload = await getPlayRoute(communityId);

                openDialog(
                    "warning",
                    {
                        title: playPayload?.title || communityTitle,
                        credits: Number.isFinite(communityPrice) ? communityPrice : 0,
                        onConfirm: async () => {
                            const result = await buyStory(playPayload.id, communityId, playPayload.url);
                            const resolvedCommunityId = Number(result?.communityId);
                            updateCommunityBalance(
                                Number.isFinite(resolvedCommunityId) && resolvedCommunityId > 0 ? resolvedCommunityId : communityId,
                                Number(result?.competitionBalance)
                            );
                            window.location.href = playPayload.url;
                        }
                    },
                    "",
                    "small"
                );
            } catch (error) {
                alert(error?.message || t("The competition could not be started."));
            } finally {
                button.disabled = false;
            }
        });
    });
}

function initRankingButtons() {
    const buttons = document.querySelectorAll(".community-ranking-button");
    buttons.forEach((button) => {
        button.addEventListener("click", (event) => {
            event.preventDefault();

            if (button.disabled) {
                return;
            }

            const communityId = Number(button.dataset.communityId);
            const communityTitle = button.dataset.communityTitle || t("Community");

            if (!communityId) {
                return;
            }

            openDialog(
                "ranking",
                { communityId },
                `Ranking - ${communityTitle}`,
                "small"
            );
        });
    });
}

function initPromotionButtons() {
    const buttons = document.querySelectorAll(".video-button");
    buttons.forEach((button) => {
        button.addEventListener("click", (event) => {
            event.preventDefault();

            const communityId = Number(button.dataset.communityId);
            if (!communityId) {
                return;
            }

            // No title: the card shows only the generated animation.
            openDialog("video", { communityId }, "", "fit");
        });
    });
}

function initDocumentaryButtons() {
    const buttons = document.querySelectorAll(".documentary-button");
    buttons.forEach((button) => {
        button.addEventListener("click", (event) => {
            event.preventDefault();

            const communityId = Number(button.dataset.communityId);
            if (!communityId) {
                return;
            }

            const communityTitle = button.dataset.communityTitle || "";

            // Documentary with voice-over: a large dialog with the full module
            // (narrator voice, scenes and editable media).
            openDialog("documentary", { communityId }, communityTitle, "large");
        });
    });
}

function initCommunityBuilderButtons() {
    const buttons = document.querySelectorAll(".community-builder-button");
    buttons.forEach((button) => {
        button.addEventListener("click", (event) => {
            event.preventDefault();
            // Admin-only: generate a whole community from a description.
            openDialog("builder", {}, button.dataset.title || "", "large");
        });
    });
}

function initRegenerateStoriesButtons() {
    const buttons = document.querySelectorAll(".community-regenerate-stories-button");
    buttons.forEach((button) => {
        button.addEventListener("click", async (event) => {
            event.preventDefault();
            const communityId = Number(button.dataset.communityId);
            if (!Number.isFinite(communityId) || communityId <= 0 || button.disabled) {
                return;
            }
            if (!window.confirm(button.dataset.confirm || "")) {
                return;
            }
            await runStoryRegeneration(button, communityId);
        });
    });
}

// Admin-only: starts the background bulk regeneration for a community and polls its
// progress into the button label until it completes or fails.
async function runStoryRegeneration(button, communityId) {
    const original = button.innerHTML;
    const progressLabel = button.dataset.progress || "";
    const setLabel = (text) => { button.innerHTML = `<i class="icon-refresh-cw icon-1x"></i> ${text}`; };

    button.disabled = true;
    setLabel(`${progressLabel}…`);

    try {
        const start = await fetch(getApiUrl(`community-builder/community/${communityId}/regenerate-stories`), {
            method: "POST",
            credentials: "same-origin",
            headers: { "Accept": "application/json", ...antiforgeryHeaders() }
        });
        const startPayload = await start.json().catch(() => ({}));
        const jobId = startPayload.job && startPayload.job.id;
        if (!start.ok || startPayload.error || !jobId) {
            throw new Error(startPayload.message || "");
        }

        for (;;) {
            await new Promise((resolve) => setTimeout(resolve, 3000));

            const status = await fetch(getApiUrl(`community-builder/regenerate-stories/${jobId}`), {
                credentials: "same-origin",
                headers: { "Accept": "application/json", "Cache-Control": "no-cache" }
            });
            if (status.status === 404) {
                throw new Error();
            }
            const payload = await status.json().catch(() => ({}));
            const job = payload.job;
            if (!status.ok || !job) {
                continue; // transient hiccup: keep polling
            }
            if (job.state === "failed") {
                throw new Error(job.error || "");
            }
            setLabel(`${progressLabel} ${job.progress || 0}%`);
            if (job.state === "completed") {
                window.alert(button.dataset.done || "");
                break;
            }
        }
    } catch {
        window.alert(button.dataset.error || "");
    } finally {
        button.disabled = false;
        button.innerHTML = original;
    }
}

function initCompetition() {
    initPlayButtons();
    initRankingButtons();
    initPromotionButtons();
    initDocumentaryButtons();
    initCommunityBuilderButtons();
    initRegenerateStoriesButtons();
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initCompetition);
} else {
    initCompetition();
}
